import { useMemo, useState } from "react";
import useSWR from "swr";
import { Plus, Trash2, Pencil, Save, X, Loader2, Upload } from "lucide-react";
import { toast } from "sonner";
import { adminApi } from "@/lib/api";

/* ---------- helpers ---------- */
const listToStr = (arr) => (arr || []).join(", ");
const strToList = (s) => s.split(",").map((x) => x.trim()).filter(Boolean);
const specsToStr = (obj) => Object.entries(obj || {}).map(([k, v]) => `${k}: ${v}`).join("\n");
const strToSpecs = (s) =>
  Object.fromEntries(s.split("\n").map((l) => l.split(/:(.*)/s)).filter((p) => p[0]?.trim()).map(([k, v]) => [k.trim(), (v || "").trim()]));
const faqsToStr = (arr) => (arr || []).map((f) => `${f.question} | ${f.answer}`).join("\n");
const strToFaqs = (s) =>
  s.split("\n").filter(Boolean).map((l) => {
    const [q, ...rest] = l.split("|");
    return { question: (q || "").trim(), answer: rest.join("|").trim() };
  }).filter((f) => f.question);

async function uploadFile(file) {
  const fd = new FormData();
  fd.append("file", file);
  return adminApi.post("/admin/upload", fd);
}

export function Field({ label, children, full = false }) {
  return (
    <div className={full ? "sm:col-span-2" : ""}>
      <label className="flabel">{label}</label>
      {children}
    </div>
  );
}

function ImageField({ label, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  return (
    <div>
      <label className="flabel">{label}</label>
      <div className="flex gap-2">
        <input className="field" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        <label className="btn-navy shrink-0 !px-3 !py-2 !text-[11px] cursor-pointer">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          <input type="file" accept=".png,.jpg,.jpeg,.webp,.pdf,.avif" className="hidden"
            onChange={async (e) => {
              const f = e.target.files?.[0];
              if (!f) return;
              setBusy(true);
              try {
                const res = await uploadFile(f);
                onChange(res.url);
                toast.success("File uploaded");
              } catch { toast.error("Upload failed"); }
              finally { setBusy(false); }
            }} />
        </label>
      </div>
      {value && /\.(png|jpe?g|webp|avif)$/i.test(value) && <img src={value} alt="preview" className="mt-2 h-16 rounded border border-line object-cover" />}
    </div>
  );
}

function GalleryField({ label, value, onChange, testid }) {
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const list = Array.isArray(value) ? value : [];
  const remove = (i) => onChange(list.filter((_, j) => j !== i));
  const addUrl = () => {
    if (url.trim()) { onChange([...list, url.trim()]); setUrl(""); }
  };
  const uploadFiles = async (files) => {
    if (!files.length) return;
    setBusy(true);
    const urls = [];
    for (const f of files) {
      try {
        const res = await uploadFile(f);
        urls.push(res.url);
      } catch { toast.error(`Upload failed: ${f.name}`); }
    }
    if (urls.length) { onChange([...list, ...urls]); toast.success(`${urls.length} photo(s) added`); }
    setBusy(false);
  };
  return (
    <div className="sm:col-span-2">
      <label className="flabel">{label}</label>
      {list.length > 0 && (
        <div className="mb-2 flex flex-wrap gap-2">
          {list.map((u, i) => (
            <div key={`${u}-${i}`} className="relative">
              <img src={u} alt={`product photo ${i + 1}`} className="h-20 w-20 rounded-lg border border-line object-cover" />
              <button type="button" onClick={() => remove(i)} data-testid={`${testid}-remove-${i}`} className="absolute -right-1.5 -top-1.5 rounded-full bg-red-600 p-0.5 text-white" aria-label="Remove photo"><X size={12} /></button>
            </div>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-2">
        <input className="field !w-56" placeholder="Paste image URL…" value={url} onChange={(e) => setUrl(e.target.value)} data-testid={testid} />
        <button type="button" onClick={addUrl} className="btn-navy !px-4 !py-2 !text-[11px]">Add</button>
        <label className="btn-gold cursor-pointer !px-4 !py-2 !text-[11px]">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload Photos
          <input type="file" accept=".png,.jpg,.jpeg,.webp,.avif" multiple className="hidden" data-testid={`${testid}-upload`} onChange={(e) => uploadFiles(Array.from(e.target.files || []))} />
        </label>
      </div>
      <p className="mt-1.5 text-xs text-mist">Add 4–5 photos per product. The first photo on the main image field is used as the card image; gallery photos show as thumbnails on the product page.</p>
    </div>
  );
}

function VideoField({ label, value, onChange, testid }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="sm:col-span-2">
      <label className="flabel">{label}</label>
      <div className="flex flex-wrap gap-2">
        <input className="field !w-64" placeholder="Paste video URL or upload" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
        <label className="btn-gold shrink-0 cursor-pointer !px-4 !py-2 !text-[11px]">
          {busy ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />} Upload Video
          <input type="file" accept=".mp4,.webm,.mov" className="hidden" data-testid={`${testid}-upload`} onChange={async (e) => {
            const f = e.target.files?.[0];
            if (!f) return;
            setBusy(true);
            try {
              const res = await uploadFile(f);
              onChange(res.url);
              toast.success("Video uploaded");
            } catch { toast.error("Upload failed — keep videos under 50MB, MP4 format"); }
            finally { setBusy(false); }
          }} />
        </label>
        {value && <button type="button" onClick={() => onChange("")} className="btn-outline !px-4 !py-2 !text-[11px]">Clear</button>}
      </div>
      {value && /\.(mp4|webm|mov)$/i.test(value) && <video src={value} muted controls className="mt-2 h-32 w-56 rounded-lg border border-line object-cover" />}
      <p className="mt-1.5 text-xs text-mist">One video per product (MP4 recommended, under 50MB).</p>
    </div>
  );
}

/* ---------- generic editor ---------- */
export function ResourceEditor({ resource, title, fields, columns, sortHint }) {
  const { data: items, mutate, isLoading } = useSWR(`/admin/${resource}`, (url) => adminApi.get(url));
  const [editing, setEditing] = useState(null); // null | {} (new) | item
  const [busy, setBusy] = useState(false);

  const startNew = () => setEditing({});
  const startEdit = (item) => setEditing({
    ...item,
    ...Object.fromEntries(fields.filter((f) => f.from).map((f) => [f.key, f.from(item[f.key])])),
  });
  const close = () => setEditing(null);

  const [aiBusy, setAiBusy] = useState({});
  const aiDraft = async (f, apply) => {
    setAiBusy((s) => ({ ...s, [f.key]: true }));
    try {
      const { text } = await adminApi.post("/ai/admin/draft", {
        kind: f.key,
        name: editing.name || editing.title || "",
        category: editing.category_slug || editing.category || "",
        notes: editing.short_description || editing.excerpt || editing.description || "",
      });
      apply(text);
      toast.success("AI draft added — review it, then Save");
    } catch (err) {
      toast.error("AI draft failed. Please try again.");
    } finally {
      setAiBusy((s) => ({ ...s, [f.key]: false }));
    }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        ...editing,
        ...Object.fromEntries(fields.filter((f) => f.to).map((f) => [f.key, f.to(editing[f.key])])),
      };
      if (editing.id) {
        await adminApi.put(`/admin/${resource}/${editing.id}`, payload);
        toast.success("Saved");
      } else {
        await adminApi.post(`/admin/${resource}`, payload);
        toast.success("Created");
      }
      mutate();
      close();
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    await adminApi.del(`/admin/${resource}/${id}`);
    toast.success("Deleted");
    mutate();
  };

  const display = (item, col) => {
    const v = item[col.key];
    if (v === true) return "Yes";
    if (v === false) return "No";
    return typeof v === "string" && v.length > 60 ? v.slice(0, 60) + "…" : (v || "—");
  };

  return (
    <div data-testid={`admin-${resource}-panel`}>
      <div className="flex items-center justify-between">
        <h2 className="font-serif text-xl font-semibold text-navy">{title} <span className="text-sm font-normal text-mist">({(items || []).length})</span></h2>
        <button onClick={startNew} className="btn-gold !px-4 !py-2 !text-[11px]" data-testid={`admin-${resource}-new-btn`}>
          <Plus size={14} /> New
        </button>
      </div>

      {editing && (
        <div className="card mt-4 p-5" data-testid={`admin-${resource}-editor`}>
          <div className="flex items-center justify-between">
            <h3 className="font-bold text-navy">{editing.id ? "Edit" : "New"} — {title}</h3>
            <button onClick={close} className="rounded-lg border border-line p-1.5" aria-label="Close editor"><X size={14} /></button>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {fields.map((f) => {
              const v = editing[f.key] ?? f.default ?? "";
              const set = (val) => setEditing((e) => ({ ...e, [f.key]: val }));
              if (f.type === "image") return <ImageField key={f.key} label={f.label} value={v} onChange={set} testid={`admin-field-${f.key}`} />;
              if (f.type === "gallery") return <GalleryField key={f.key} label={f.label} value={v} onChange={set} testid={`admin-field-${f.key}`} />;
              if (f.type === "video") return <VideoField key={f.key} label={f.label} value={v} onChange={set} testid={`admin-field-${f.key}`} />;
              if (f.type === "textarea") return (
                <div key={f.key} className="sm:col-span-2">
                  <div className="flex items-center justify-between gap-2">
                    <label className="flabel">{f.label}</label>
                    {f.ai && (
                      <button type="button" onClick={() => aiDraft(f, set)} disabled={aiBusy[f.key]} data-testid={`admin-ai-${f.key}`} className="text-[11px] font-bold uppercase tracking-wider text-gold-dark hover:text-navy disabled:opacity-50">
                        {aiBusy[f.key] ? "Drafting…" : "✨ AI Draft"}
                      </button>
                    )}
                  </div>
                  <textarea className="field min-h-24" rows={f.rows || 4} value={v} onChange={(e) => set(e.target.value)} data-testid={`admin-field-${f.key}`} />
                </div>
              );
              if (f.type === "bool") return (
                <Field key={f.key} label={f.label}>
                  <select className="field" value={String(!!v)} onChange={(e) => set(e.target.value === "true")} data-testid={`admin-field-${f.key}`}>
                    <option value="true">Yes</option><option value="false">No</option>
                  </select>
                </Field>
              );
              return <Field key={f.key} label={f.label}><input className="field" type={f.type || "text"} value={v} onChange={(e) => set(e.target.value)} data-testid={`admin-field-${f.key}`} /></Field>;
            })}
          </div>
          <button onClick={save} disabled={busy} className="btn-gold mt-5" data-testid={`admin-${resource}-save-btn`}>
            {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save
          </button>
        </div>
      )}

      {isLoading ? <div className="card mt-4 h-32 animate-pulse" /> : (
        <div className="mt-4 space-y-2">
          {(items || []).length === 0 && <div className="card p-6 text-sm text-mist">No items yet — create the first one.</div>}
          {(items || []).map((item) => (
            <div key={item.id} className="card flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`admin-${resource}-row-${item.id}`}>
              <div className="min-w-0 text-sm">
                <span className="font-bold text-navy">{item[columns[0].key]}</span>
                {columns.slice(1).map((c) => <span key={c.key} className="ml-2 text-xs text-mist">· {display(item, c)}</span>)}
              </div>
              <div className="flex gap-2">
                <button onClick={() => startEdit(item)} data-testid={`admin-${resource}-edit-${item.id}`} className="rounded-lg border border-line p-2 text-navy hover:border-gold" aria-label="Edit"><Pencil size={13} /></button>
                <button onClick={() => remove(item.id)} data-testid={`admin-${resource}-delete-${item.id}`} className="rounded-lg border border-line p-2 text-red-600 hover:border-red-300" aria-label="Delete"><Trash2 size={13} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- specific panels ---------- */
const PRODUCT_FIELDS = [
  { key: "name", label: "Product Name" },
  { key: "slug", label: "Slug (URL)" },
  { key: "category_slug", label: "Category Slug (rice/spices/makhana/dehydrated-fruits-vegetables/peanuts/tea/food-powders)" },
  { key: "order", label: "Display Order", type: "number" },
  { key: "short_description", label: "Short Description", full: true },
  { key: "description", label: "Detailed Description", type: "textarea", ai: true },
  { key: "description_more", label: "Additional Description (shown below the overview — add more detail when needed)", type: "textarea", rows: 6, ai: true },
  { key: "image", label: "Product Image (main photo — used on cards)", type: "image" },
  { key: "gallery", label: "Product Photos (add 4–5)", type: "gallery" },
  { key: "video_url", label: "Product Video", type: "video" },
  { key: "origin", label: "Origin" },
  { key: "grades", label: "Available Grades (comma separated)", to: strToList, from: listToStr },
  { key: "specifications", label: "Specifications (one per line — Key: Value)", type: "textarea", to: strToSpecs, from: specsToStr },
  { key: "packaging", label: "Packaging", full: true },
  { key: "moq", label: "MOQ" },
  { key: "shelf_life", label: "Shelf Life" },
  { key: "storage_conditions", label: "Storage Conditions" },
  { key: "hs_code", label: "HS Code" },
  { key: "markets", label: "Available Markets (comma separated)", to: strToList, from: listToStr },
  { key: "private_label", label: "Private Label Availability", full: true },
  { key: "sample_availability", label: "Sample Availability", full: true },
  { key: "applications", label: "Applications (comma separated)", to: strToList, from: listToStr },
  { key: "certificates", label: "Certificates (comma separated)", to: strToList, from: listToStr },
  { key: "export_logistics", label: "Export / Logistics", type: "textarea" },
  { key: "faqs", label: "Product FAQs (one per line — Question | Answer)", type: "textarea", to: strToFaqs, from: faqsToStr },
  { key: "seo_title", label: "SEO Title", full: true },
  { key: "seo_description", label: "SEO Description", type: "textarea", rows: 2, ai: true },
  { key: "featured", label: "Featured on Home", type: "bool" },
  { key: "published", label: "Published", type: "bool" },
];

export function ProductsPanel() {
  return <ResourceEditor resource="products" title="Products" fields={PRODUCT_FIELDS}
    columns={[{ key: "name" }, { key: "category_slug" }, { key: "slug" }, { key: "featured" }, { key: "published" }]} />;
}

export function CategoriesPanel() {
  return <ResourceEditor resource="categories" title="Categories"
    fields={[
      { key: "name", label: "Name" },
      { key: "slug", label: "Slug (URL)" },
      { key: "tagline", label: "Tagline" },
      { key: "order", label: "Display Order", type: "number" },
      { key: "description", label: "Description", type: "textarea", rows: 2 },
      { key: "image", label: "Category Image", type: "image" },
      { key: "published", label: "Published", type: "bool" },
    ]}
    columns={[{ key: "name" }, { key: "slug" }, { key: "order" }]} />;
}

export function PostsPanel() {
  return <ResourceEditor resource="posts" title="Insights Articles"
    fields={[
      { key: "title", label: "Title", full: true },
      { key: "slug", label: "Slug (URL)" },
      { key: "category", label: "Category" },
      { key: "author", label: "Author" },
      { key: "cover_image", label: "Cover Image", type: "image" },
      { key: "excerpt", label: "Excerpt", type: "textarea", rows: 2, ai: true },
      { key: "content", label: "Content (HTML — use <p> and <h2> tags)", type: "textarea", rows: 12, ai: true },
      { key: "published", label: "Published", type: "bool" },
    ]}
    columns={[{ key: "title" }, { key: "category" }, { key: "published" }]} />;
}

export function FaqsPanel() {
  return <ResourceEditor resource="faqs" title="FAQs"
    fields={[
      { key: "question", label: "Question", full: true },
      { key: "answer", label: "Answer", type: "textarea", rows: 3 },
      { key: "order", label: "Display Order", type: "number" },
      { key: "published", label: "Published", type: "bool" },
    ]}
    columns={[{ key: "question" }, { key: "order" }, { key: "published" }]} />;
}

export function CertificatesPanel() {
  return <ResourceEditor resource="certificates" title="Certificates"
    fields={[
      { key: "name", label: "Certification Name" },
      { key: "number", label: "Certificate Number" },
      { key: "authority", label: "Issuing Authority" },
      { key: "issue_date", label: "Issue Date (YYYY-MM-DD)" },
      { key: "expiry_date", label: "Expiry Date (YYYY-MM-DD)" },
      { key: "pdf_url", label: "Certificate PDF", type: "image" },
      { key: "published", label: "Published (visible on site)", type: "bool" },
    ]}
    columns={[{ key: "name" }, { key: "authority" }, { key: "published" }]} />;
}

export function TestimonialsPanel() {
  return <ResourceEditor resource="testimonials" title="Testimonials"
    fields={[
      { key: "client_name", label: "Client Name" },
      { key: "company", label: "Company" },
      { key: "country", label: "Country" },
      { key: "rating", label: "Rating (1–5, shown as stars)", type: "number" },
      { key: "text", label: "Testimonial (what the client said)", type: "textarea", rows: 3 },
      { key: "photo_url", label: "Client Photo (optional)", type: "image" },
      { key: "order", label: "Display Order", type: "number" },
      { key: "published", label: "Published (visible on site)", type: "bool" },
    ]}
    columns={[{ key: "client_name" }, { key: "company" }, { key: "country" }, { key: "published" }]} />;
}

export function DocumentsPanel() {
  return <ResourceEditor resource="documents" title="Company Documents"
    fields={[
      { key: "title", label: "Document Title" },
      { key: "order", label: "Display Order", type: "number" },
      { key: "description", label: "Short Description", full: true },
      { key: "file_url", label: "Document File (PDF or image)", type: "image" },
      { key: "published", label: "Published (visible on site)", type: "bool" },
    ]}
    columns={[{ key: "title" }, { key: "order" }, { key: "published" }]} />;
}
